import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

// Type for marker with extended properties
type ExtendedMarker = {
  listingId?: string;
  price?: number;
} & any;

// Space type mapping for UI labels
const SPACE_TYPE_LABELS: Record<string, string> = {
  garage: "Garage",
  shed: "Shed",
  warehouse: "Warehouse",
  basement: "Basement",
  container: "Container",
  parking: "Parking Space",
  caravan_boat: "Caravan or Boat Storage",
  land: "Acreage / Land",
  pontoon: "Pontoon / Jetty",
};

export default function Browse() {
  const [searchParams] = useSearchParams();
  const initialView = 'list';
  const initialLocation = searchParams.get('location') || '';
  
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchLocation, setSearchLocation] = useState(initialLocation);
  const [searchId, setSearchId] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'map'>(initialView);
  const [hoveredListingId, setHoveredListingId] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [visibleListingIds, setVisibleListingIds] = useState<string[]>([]);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<ExtendedMarker[]>([]);
  const overlayRef = useRef<any>(null);
  const selectedMarkerRef = useRef<any>(null);

  // Supabase state
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Filter states
  const [showPriceFilter, setShowPriceFilter] = useState(false);
  const [showSizeFilter, setShowSizeFilter] = useState(false);
  const [showExposureFilter, setShowExposureFilter] = useState(false);
  const [showFeaturesFilter, setShowFeaturesFilter] = useState(false);
  const [showDurationFilter, setShowDurationFilter] = useState(false);
  const [showSortFilter, setShowSortFilter] = useState(false);

  // Filter values
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(10000);
  const [priceError, setPriceError] = useState<string>('');
  const [minPriceInput, setMinPriceInput] = useState<string>('0');
  const [maxPriceInput, setMaxPriceInput] = useState<string>('10000');
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedExposure, setSelectedExposure] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<number[]>([]);
  const [customDurationEnabled, setCustomDurationEnabled] = useState(false);
  const [customDurationValue, setCustomDurationValue] = useState('');
  const [sortBy, setSortBy] = useState('relevance');

  const categories = [
    'All',
    'Storage Spaces',
    'Vehicle Spaces',
    'Garage Space',
    'Boat/Jet Ski Spaces',
    'Jetty/Pontoon Space',
    'Caravan Spaces',
    'Land/Acreage'
  ];

  // Size options in metres
  const sizeOptions = [
    'Under 10m²',
    '10-25m²',
    '25-50m²',
    '50-100m²',
    '100-200m²',
    'Over 200m²'
  ];

  // Exposure options
  const exposureOptions = [
    'Indoor',
    'Outdoor - covered',
    'Outdoor - uncovered'
  ];

  // Features list
  const featuresList = [
    '24/7 Access',
    'Alarm System',
    'Bathroom Access',
    'Climate Controlled',
    'Covered',
    'Drive-Through',
    'Drive-up Access',
    'Electricity',
    'Fire Safety',
    'Forklift Access',
    'Gated Entry',
    'Ground Floor',
    'High Ceiling',
    'Indoor Storage',
    'Lift Access',
    'Lighting',
    'Loading Dock',
    'Office Space',
    'On-Site Manager',
    'Outdoor Storage',
    'Package Acceptance',
    'Pallet Storage',
    'Pest Control',
    'Phone Reception',
    'Racking System',
    'Security Cameras',
    'Separate Access',
    'Shelving Available',
    'Ventilation',
    'Water Access',
    'Wheelchair Access',
    'Wide Entry',
    'WiFi Available'
  ];

  // Sort options
  const sortOptions = [
    { value: 'relevance', label: 'Sort by' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest First' },
    { value: 'distance', label: 'Distance' }
  ];

  // Duration options (1-36 months)
  const durationOptions = [
    { value: 1, label: '1 month' },
    { value: 2, label: '2 months' },
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 9, label: '9 months' },
    { value: 12, label: '12 months' },
    { value: 18, label: '18 months' },
    { value: 24, label: '24 months' }
  ];

  // Fetch listings from Supabase
  const fetchListings = async () => {
    setLoading(true);
    setFetchError(null);
    
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .or('status.eq.active,status.is.null')
      .order('created_at', { ascending: false });
    
    
    if (error) {
      setFetchError(error.message);
      setListings([]);
    } else {
      // Fetch active bookings to determine availability
      const listingIds = (data || []).map(l => l.id);
      let availabilityMap: Record<string, string | null> = {};
      if (listingIds.length > 0) {
        const { data: bookings } = await supabase
          .from('bookings')
          .select('listing_id, end_date')
          .in('listing_id', listingIds)
          .in('status', ['active', 'confirmed'])
          .order('end_date', { ascending: false });
        if (bookings) {
          bookings.forEach(b => {
            if (!availabilityMap[b.listing_id]) {
              availabilityMap[b.listing_id] = b.end_date;
            }
          });
        }
      }

      // Map Supabase data to existing card format
      const mappedListings = (data || []).map(listing => ({
        id: listing.id,
        title: listing.title,
        space_type: listing.space_type,
        category: SPACE_TYPE_LABELS[listing.space_type] || "Storage Space",
        location: `${listing.suburb}, ${listing.state}`,
        price: listing.price_per_month,
        rating: 4.8, // Default until reviews implemented
        reviews: 0,
        image: listing.images?.[0] || 'https://readdy.ai/api/search-image?query=modern%20clean%20storage%20space%20interior%20with%20simple%20white%20background%20professional%20photography%20bright%20lighting%20minimalist%20design%20organized%20empty%20room%20ready%20for%20storage%20high%20quality%20commercial%20real%20estate%20photo&width=800&height=600&seq=browse-fallback&orientation=landscape',
        coordinates: (listing.latitude && listing.longitude) 
          ? { lat: listing.latitude, lng: listing.longitude }
          : null,
        size: `${listing.size_sqm}m²`,
        exposure: listing.exposure || 'Indoor',
        features: listing.features || [],
        firstMonthDiscount: listing.first_month_discount || false,
        availableFrom: availabilityMap[listing.id] || null
      }));
      setListings(mappedListings);
    }
    
    setLoading(false);
  };

  // Fetch on mount
  useEffect(() => {
    fetchListings();
  }, []);

  // Filter functions
  const getSizeCategory = (size: string) => {
    const numericSize = parseInt(size.replace('m²', ''));
    if (numericSize < 10) return 'Under 10m²';
    if (numericSize <= 25) return '10-25m²';
    if (numericSize <= 50) return '25-50m²';
    if (numericSize <= 100) return '50-100m²';
    if (numericSize <= 200) return '100-200m²';
    return 'Over 200m²';
  };

  const handleSizeToggle = (size: string) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const handleExposureToggle = (exposure: string) => {
    setSelectedExposure(prev => 
      prev.includes(exposure) 
        ? prev.filter(e => e !== exposure)
        : [...prev, exposure]
    );
  };

  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handleDurationToggle = (duration: number) => {
    setSelectedDuration(prev => 
      prev.includes(duration) 
        ? prev.filter(d => d !== duration)
        : [...prev, duration]
    );
  };

  // Filter listings based on all criteria
  const filteredListings = listings.filter(listing => {
    const matchesCategory = !selectedCategory || selectedCategory === 'All' || listing.space_type === selectedCategory;
    
    // Location matching
    let matchesLocation = true;
    if (searchLocation !== '') {
      const searchLower = searchLocation.toLowerCase();
      const listingLocation = listing.location.toLowerCase();
      const searchParts = searchLower.split(',').map(part => part.trim());
      matchesLocation = searchParts.some(part => {
        if (/^\d+$/.test(part)) {
          return listingLocation.includes(part);
        }
        return listingLocation.includes(part);
      }) || listingLocation.includes(searchLower);
    }
    
    // ID matching
    const matchesId = searchId === '' || 
      listing.id.toLowerCase().includes(searchId.toLowerCase());

    // Price filtering with type casting and validation
    const price = Number(listing.price) || 0;
    const priceRangeValid = maxPrice === 0 || maxPrice >= minPrice;
    const matchesPrice = !priceRangeValid ? true : (price >= minPrice && (maxPrice === 0 || price <= maxPrice));
    


    // First month discount filtering
    const matchesDiscount = !firstMonthDiscount || listing.firstMonthDiscount;

    // Size filtering
    const matchesSize = selectedSizes.length === 0 || 
      selectedSizes.includes(getSizeCategory(listing.size));

    // Exposure filtering
    const matchesExposure = selectedExposure.length === 0 || 
      selectedExposure.includes(listing.exposure);

    // Features filtering
    const matchesFeatures = selectedFeatures.length === 0 || 
      selectedFeatures.every(feature => listing.features.includes(feature));

    // Duration filtering - always show all listings since any space can be rented for any duration
    const matchesDuration = true;

    return matchesCategory && matchesLocation && matchesId && matchesPrice && 
           matchesDiscount && matchesSize && matchesExposure && matchesFeatures && matchesDuration;
  });

  // Sort listings
  const sortedListings = [...filteredListings].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'newest':
        return 0; // Would need creation date in real app
      case 'distance':
        return 0; // Would need user location in real app
      default:
        return 0;
    }
  });

  // Filter listings by map bounds when in map view
  const displayedListings = viewMode === 'map'
    ? sortedListings.filter(listing => visibleListingIds.includes(listing.id))
    : sortedListings;

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowPriceFilter(false);
      setShowSizeFilter(false);
      setShowExposureFilter(false);
      setShowFeaturesFilter(false);
      setShowDurationFilter(false);
      setShowSortFilter(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Load Google Maps script dynamically
  useEffect(() => {
    if (viewMode === 'map' && !mapLoaded && !mapError) {
      const loadGoogleMaps = () => {
        if (window.google && window.google.maps) {
          setMapLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDOerzhhGJ4THjQkdH02A1OWxyMcrduJLw&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setMapLoaded(true);
        };
        script.onerror = () => {
          setMapError(true);
        };
        document.head.appendChild(script);
      };

      loadGoogleMaps();
    }
  }, [viewMode, mapLoaded, mapError]);

  // Initialize and update Google Map - FIXED: Re-initialize when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && mapLoaded && mapRef.current && sortedListings.length > 0 && window.google) {
      try {
        // Create map if it doesn't exist OR if we switched back to map view
        if (!mapInstanceRef.current || !mapInitialized) {
          // Clear any existing map instance
          if (mapInstanceRef.current) {
            mapInstanceRef.current = null;
          }

          const listingsWithCoords = sortedListings.filter(l => l.coordinates);
          const defaultCenter = listingsWithCoords.length > 0
            ? { lat: listingsWithCoords.reduce((sum, l) => sum + l.coordinates!.lat, 0) / listingsWithCoords.length,
                lng: listingsWithCoords.reduce((sum, l) => sum + l.coordinates!.lng, 0) / listingsWithCoords.length }
            : { lat: -25.2744, lng: 133.7751 };
          const defaultZoom = listingsWithCoords.length > 0 ? 6 : 4;
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: defaultCenter,
            zoom: defaultZoom,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ],
            gestureHandling: 'greedy',
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true
          });

          // Add bounds_changed listener to update visible listings
          mapInstanceRef.current.addListener('bounds_changed', () => {
            updateVisibleListings();
          });

          // Add zoom_changed listener to update marker styles
          mapInstanceRef.current.addListener('zoom_changed', () => {
            updateMarkerStyles();
          });

          // Add click listener to map to close popup when clicking on map
          mapInstanceRef.current.addListener('click', () => {
            setSelectedListing(null);
            setPopupPosition(null);
            selectedMarkerRef.current = null;
          });

          setMapInitialized(true);
        }

        // Only clear and recreate markers if the set of listing IDs with coordinates has changed
        const currentListingIds = sortedListings.filter(l => l.coordinates !== null).map(l => l.id).sort().join(',');
        const existingListingIds = markersRef.current.map((m) => m.listingId).sort().join(',');

        if (currentListingIds !== existingListingIds && currentListingIds.length > 0) {
          // Clear existing markers
          markersRef.current.forEach(marker => marker.setMap(null));
          markersRef.current = [];

          // Create new markers - ONLY for listings with coordinates
          sortedListings.forEach((listing) => {
            if (!listing.coordinates) return; // Skip listings without coordinates
            
            const position = new window.google.maps.LatLng(listing.coordinates.lat, listing.coordinates.lng);
            const currentZoom = mapInstanceRef.current?.getZoom() || 11;
            
            // Create marker with initial icon based on zoom level
            const markerIcon = getMarkerIcon(listing.price, false, currentZoom);
            const marker = new window.google.maps.Marker({
              position: position,
              map: mapInstanceRef.current!,
              icon: markerIcon,
              title: listing.title,
              optimized: false,
              cursor: 'pointer'
            });

            // Add click listener to show custom popup near marker
            marker.addListener('click', () => {
              setSelectedListing(listing);
              selectedMarkerRef.current = marker;
              
              // Calculate initial popup position
              const projection = mapInstanceRef.current!.getProjection();
              if (projection && mapRef.current) {
                const bounds = mapInstanceRef.current!.getBounds();
                if (bounds) {
                  const ne = bounds.getNorthEast();
                  const sw = bounds.getSouthWest();
                  const neWorldCoord = projection.fromLatLngToPoint(ne);
                  const swWorldCoord = projection.fromLatLngToPoint(sw);
                  const worldCoordinate = projection.fromLatLngToPoint(position);
                  
                  if (neWorldCoord && swWorldCoord && worldCoordinate) {
                    const mapWidth = mapRef.current.offsetWidth;
                    const mapHeight = mapRef.current.offsetHeight;
                    
                    const pixelX = ((worldCoordinate.x - swWorldCoord.x) / (neWorldCoord.x - swWorldCoord.x)) * mapWidth;
                    const pixelY = ((worldCoordinate.y - neWorldCoord.y) / (swWorldCoord.y - neWorldCoord.y)) * mapHeight;
                    
                    setPopupPosition({ x: pixelX, y: pixelY });
                  }
                }
              }
            });

            // Store marker with listing id for hover updates
            (marker as any).listingId = listing.id;
            (marker as any).price = listing.price;
            markersRef.current.push(marker as ExtendedMarker);
          });

          updateVisibleListings();
        }
      } catch (error) {
        setMapError(true);
      }
    }
  }, [viewMode, mapLoaded, sortedListings, mapInitialized]);

  // Reset map initialization when switching away from map view
  useEffect(() => {
    if (viewMode === 'list') {
      setMapInitialized(false);
      setSelectedListing(null);
      setPopupPosition(null);
      selectedMarkerRef.current = null;
    }
  }, [viewMode]);

  // Helper function to get marker icon - NOW USING STASHLY LOGO
  const getMarkerIcon = (price: number, isHovered: boolean, zoom: number) => {
    if (!window.google || !window.google.maps) {
      return undefined;
    }

    const useSimpleLogo = zoom < 12; // Use simple logo when zoomed out
    
    if (useSimpleLogo) {
      // Simple Stashly logo for zoomed out view - with hover effect
      return {
        url: 'https://static.readdy.ai/image/ecfc40ffeefdf135692cc83520a4b16c/83ef8aa1c3e38301e29b72d6b31cc93d.png',
        scaledSize: new window.google.maps.Size(isHovered ? 36 : 30, isHovered ? 36 : 30),
        anchor: new window.google.maps.Point(isHovered ? 18 : 15, isHovered ? 18 : 15)
      };
    } else {
      // Stashly logo with price badge for zoomed in view - FIXED TO USE STASHLY BLUE
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="100" height="60">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <!-- Price badge background - NOW USING STASHLY BLUE #2563EB -->
            <rect x="10" y="25" width="80" height="35" rx="17.5" fill="${isHovered ? '#000000' : '#2563EB'}" filter="url(#shadow)" />
            <!-- Price text -->
            <text x="50" y="47" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="white" text-anchor="middle">$${price}</text>
            <!-- Logo circle background -->
            <circle cx="50" cy="15" r="15" fill="white" filter="url(#shadow)" />
            <!-- Stashly logo icon (simplified house with keyhole) -->
            <g transform="translate(50, 15) scale(0.5)">
              <path d="M-12,-8 L0,-16 L12,-8 L12,12 L-12,12 Z" fill="${isHovered ? '#000000' : '#2563EB'}" stroke="none"/>
              <circle cx="0" cy="2" r="4" fill="white"/>
              <path d="M-2,2 L-2,6 L2,6 L2,2" fill="white"/>
            </g>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(100, 60),
        anchor: new window.google.maps.Point(50, 60)
      };
    }
  };

  // Function to update marker styles based on zoom level
  const updateMarkerStyles = () => {
    if (!mapInstanceRef.current || !window.google) return;
    
    const currentZoom = mapInstanceRef.current.getZoom() || 11;
    
    requestAnimationFrame(() => {
      markersRef.current.forEach((marker) => {
        const isHovered = (marker as any).listingId === hoveredListingId;
        const price = (marker as any).price || 0;
        const newIcon = getMarkerIcon(price, isHovered, currentZoom);
        
        if (newIcon) {
          const currentIcon = marker.getIcon();
          if (!currentIcon || (typeof currentIcon === 'object' && 'url' in currentIcon && currentIcon.url !== newIcon.url)) {
            marker.setIcon(newIcon);
          }
        }
      });
    });
  };

  // Improved hover effect for markers - with immediate updates
  useEffect(() => {
    if (viewMode === 'map' && markersRef.current.length > 0 && mapInstanceRef.current && window.google) {
      const currentZoom = mapInstanceRef.current.getZoom() || 11;
      
      // Immediately update all marker icons
      markersRef.current.forEach((marker) => {
        const isHovered = (marker as any).listingId === hoveredListingId;
        const price = (marker as any).price || 0;
        const newIcon = getMarkerIcon(price, isHovered, currentZoom);
        
        if (newIcon) {
          // Always update the icon to ensure hover state changes are visible
          marker.setIcon(newIcon);
        }
      });
    }
  }, [hoveredListingId, viewMode]);

  // Function to update visible listings based on map bounds
  const updateVisibleListings = () => {
    if (!mapInstanceRef.current || !window.google) return;

    const bounds = mapInstanceRef.current.getBounds();
    if (!bounds) return;

    const currentListings = sortedListings;
    const visible = currentListings
      .filter(listing => {
        if (!listing.coordinates) return false; // Skip listings without coordinates
        const position = new window.google.maps.LatLng(listing.coordinates.lat, listing.coordinates.lng);
        return bounds.contains(position);
      })
      .map(listing => listing.id);

    setVisibleListingIds(visible);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-36">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Browse All Spaces</h1>
          <p className="text-gray-600">
            {viewMode === 'map' && visibleListingIds.length > 0
              ? `${displayedListings.length} spaces visible in map area`
              : `${sortedListings.length} spaces available`}
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <i className="ri-map-pin-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              placeholder="Search location..."
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 relative">
            <i className="ri-hashtag absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              placeholder="Search by ID number..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full md:w-64 px-4 py-3 border-2 border-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer bg-white"
            >
              <option value="">All</option>
              <option value="garage">Garage</option>
              <option value="shed">Shed</option>
              <option value="warehouse">Warehouse</option>
              <option value="basement">Basement</option>
              <option value="container">Container</option>
              <option value="parking">Parking Space</option>
              <option value="caravan_boat">Caravan or Boat Storage</option>
              <option value="land">Acreage / Land</option>
              <option value="pontoon">Pontoon / Jetty</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"></i>
          </div>
        </div>

        {/* Filter Bar and View Toggle */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          {/* Filter Bar */}
          <div className="grid grid-cols-2 gap-2 order-2 lg:order-1 lg:flex lg:flex-wrap lg:items-center lg:gap-3 w-full lg:w-auto">
            {/* Price Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPriceFilter(!showPriceFilter);
                  setShowSizeFilter(false);
                  setShowExposureFilter(false);
                  setShowFeaturesFilter(false);
                  setShowDurationFilter(false);
                  setShowSortFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <i className="ri-money-dollar-circle-line text-gray-600"></i>
                <span className="text-xs font-medium text-gray-700">Price</span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>
              
              {showPriceFilter && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-semibold text-gray-900 mb-4">Price Range</h3>
                  
                  {/* Price Range Inputs */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum price
                    </label>
                    <div className="relative mb-4">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="10"
                        value={minPriceInput}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setMinPriceInput(inputValue);
                          const value = inputValue === '' ? 0 : parseInt(inputValue);
                          setMinPrice(value);
                          if (maxPrice > 0 && value > maxPrice) {
                            setPriceError('Minimum cannot exceed maximum');
                          } else {
                            setPriceError('');
                          }
                        }}
                        onBlur={() => {
                          if (minPriceInput === '') {
                            setMinPriceInput('0');
                            setMinPrice(0);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum price
                    </label>
                    <div className="relative mb-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="10"
                        value={maxPriceInput}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setMaxPriceInput(inputValue);
                          const value = inputValue === '' ? 0 : parseInt(inputValue);
                          setMaxPrice(value);
                          if (value > 0 && value < minPrice) {
                            setPriceError('Maximum cannot be less than minimum');
                          } else {
                            setPriceError('');
                          }
                        }}
                        onBlur={() => {
                          if (maxPriceInput === '') {
                            setMaxPriceInput('10000');
                            setMaxPrice(10000);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="No maximum"
                      />
                    </div>
                    
                    {priceError && (
                      <p className="text-xs text-red-600 mb-2">{priceError}</p>
                    )}
                  </div>

                  {/* 50% off 1st Month Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="firstMonthDiscount"
                      checked={firstMonthDiscount}
                      onChange={(e) => setFirstMonthDiscount(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="firstMonthDiscount" className="ml-2 text-sm text-gray-700 cursor-pointer">
                      50% off 1st month
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Size Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSizeFilter(!showSizeFilter);
                  setShowPriceFilter(false);
                  setShowExposureFilter(false);
                  setShowFeaturesFilter(false);
                  setShowDurationFilter(false);
                  setShowSortFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <i className="ri-ruler-line text-gray-600"></i>
                <span className="text-xs font-medium text-gray-700">Size</span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>

              {showSizeFilter && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-gray-900 mb-4">Size Range</h3>
                  <div className="space-y-2">
                    {sizeOptions.map((size) => (
                      <label key={size} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedSizes.includes(size)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSizeToggle(size);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">{size}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Exposure Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExposureFilter(!showExposureFilter);
                  setShowPriceFilter(false);
                  setShowSizeFilter(false);
                  setShowFeaturesFilter(false);
                  setShowDurationFilter(false);
                  setShowSortFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <i className="ri-sun-line text-gray-600"></i>
                <span className="text-xs font-medium text-gray-700">Exposure</span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>

              {showExposureFilter && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-gray-900 mb-4">Exposure Type</h3>
                  <div className="space-y-2">
                    {exposureOptions.map((exposure) => (
                      <label key={exposure} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedExposure.includes(exposure)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleExposureToggle(exposure);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">{exposure}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Features Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFeaturesFilter(!showFeaturesFilter);
                  setShowPriceFilter(false);
                  setShowSizeFilter(false);
                  setShowExposureFilter(false);
                  setShowDurationFilter(false);
                  setShowSortFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <i className="ri-settings-3-line text-gray-600"></i>
                <span className="text-xs font-medium text-gray-700">Features</span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>

              {showFeaturesFilter && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-gray-900 mb-4">Amenities & Features</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {featuresList.map((feature) => (
                      <label key={feature} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedFeatures.includes(feature)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFeatureToggle(feature);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="ml-2 text-xs text-gray-700">{feature}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Duration Filter */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDurationFilter(!showDurationFilter);
                  setShowPriceFilter(false);
                  setShowSizeFilter(false);
                  setShowExposureFilter(false);
                  setShowFeaturesFilter(false);
                  setShowSortFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <i className="ri-calendar-line text-gray-600"></i>
                <span className="text-xs font-medium text-gray-700">Duration</span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>

              {showDurationFilter && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-semibold text-gray-900 mb-4">Rental Term</h3>
                  <div className="space-y-2 mb-4">
                    {durationOptions.map((option) => (
                      <label key={option.value} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDuration.includes(option.value)}
                          onChange={() => handleDurationToggle(option.value)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Custom Duration Option */}
                  <div className="pt-4 border-t border-gray-200">
                    <label className="flex items-center cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={customDurationEnabled}
                        onChange={(e) => {
                          setCustomDurationEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setCustomDurationValue('');
                          }
                        }}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Custom</span>
                    </label>
                    
                    {customDurationEnabled && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="999"
                          value={customDurationValue}
                          onChange={(e) => setCustomDurationValue(e.target.value)}
                          placeholder="e.g., 43"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <span className="text-sm text-gray-600">months</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sort by Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSortFilter(!showSortFilter);
                  setShowPriceFilter(false);
                  setShowSizeFilter(false);
                  setShowExposureFilter(false);
                  setShowFeaturesFilter(false);
                  setShowDurationFilter(false);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-white w-full lg:w-auto"
              >
                <span className="text-xs font-medium text-gray-700">
                  {sortOptions.find(opt => opt.value === sortBy)?.label}
                </span>
                <i className="ri-arrow-down-s-line text-gray-500"></i>
              </button>

              {showSortFilter && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortFilter(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                        sortBy === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* View Toggle */}
          <div className="inline-flex rounded-lg border-2 border-gray-300 p-1 order-1 lg:order-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-6 py-2 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-list-check mr-2"></i>
              List View
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-6 py-2 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
                viewMode === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-map-pin-line mr-2"></i>
              Map View
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600">Loading spaces...</p>
          </div>
        )}

        {/* Error State */}
        {fetchError && (
          <div className="text-center py-16 bg-red-50 rounded-xl">
            <i className="ri-error-warning-line text-6xl text-red-500 mb-4"></i>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Spaces</h3>
            <p className="text-gray-600 mb-4">{fetchError}</p>
            <button
              onClick={fetchListings}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Content Area */}
        {!loading && !fetchError && sortedListings.length > 0 ? (
          viewMode === 'list' ? (
            // List View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedListings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={listing.image}
                      alt={listing.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = 'https://readdy.ai/api/search-image?query=modern%20clean%20storage%20space%20interior%20professional%20photography%20bright%20lighting&width=800&height=600&seq=browse-fallback&orientation=landscape';
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-blue-600 px-3 py-1 rounded-full shadow-md">
                      <span className="text-sm font-bold text-white">${listing.price}/mo</span>
                    </div>
                    {listing.firstMonthDiscount && (
                      <div className="absolute top-4 left-4 bg-red-500 px-2 py-1 rounded-full shadow-md">
                        <span className="text-xs font-bold text-white">50% off 1st month</span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        {listing.category}
                      </span>
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-400 text-sm mr-1"></i>
                        <span className="text-sm font-semibold text-gray-900">{listing.rating}</span>
                        <span className="text-xs text-gray-500 ml-1">({listing.reviews})</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {listing.title}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <i className="ri-map-pin-line mr-1 text-sm"></i>
                      <span className="text-sm">{listing.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <i className="ri-expand-diagonal-line mr-1"></i>
                      <span>{listing.size}</span>
                      <span className="mx-2">•</span>
                      <span>{listing.exposure}</span>
                    </div>
                    <div className="mt-3">
                      {listing.availableFrom ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                          <i className="ri-calendar-line"></i>
                          Available {new Date(listing.availableFrom) > new Date()
                            ? `from ${new Date(listing.availableFrom).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : 'Now'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                          <i className="ri-checkbox-circle-line"></i>
                          Available Now
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            // Map View - Mobile First Layout
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Map - Full width on mobile, right side on desktop */}
              <div className="w-full lg:flex-1 h-[400px] lg:h-[calc(100vh-400px)] lg:min-h-[600px] order-1 lg:order-2 rounded-2xl overflow-hidden shadow-lg bg-gray-100 sticky top-24 z-10 relative">
                {mapError ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50">
                    <div className="text-center p-8">
                      <i className="ri-map-pin-line text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Map Unavailable</h3>
                      <p className="text-gray-600">Unable to load the map at this time.</p>
                      <p className="text-sm text-gray-500 mt-2">Please use List View to browse spaces.</p>
                    </div>
                  </div>
                ) : !mapLoaded ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
                      <p className="text-gray-600">Loading map...</p>
                    </div>
                  </div>
                ) : (
                  <div ref={mapRef} className="w-full h-full"></div>
                )}

                {/* Custom Popup Overlay - Positioned near marker and follows it */}
                {selectedListing && popupPosition && (
                  <div 
                    className="absolute z-50 pointer-events-none"
                    style={{
                      left: `${popupPosition.x}px`,
                      top: `${popupPosition.y}px`,
                      transform: 'translate(-50%, -100%)',
                      marginTop: '-15px'
                    }}
                  >
                    <div 
                      className="bg-white rounded-xl shadow-2xl w-52 overflow-hidden pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Close Button */}
                      <button
                        onClick={() => {
                          setSelectedListing(null);
                          setPopupPosition(null);
                          selectedMarkerRef.current = null;
                        }}
                        className="absolute top-1.5 right-1.5 z-10 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-lg text-gray-700"></i>
                      </button>

                      {/* Content */}
                      <Link to={`/listing/${selectedListing.id}`} className="block">
                        <div className="relative w-full h-32 overflow-hidden">
                          <img
                            src={selectedListing.image}
                            alt={selectedListing.title}
                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                          />
                          {selectedListing.firstMonthDiscount && (
                            <div className="absolute top-2 left-2 bg-red-500 px-1.5 py-0.5 rounded-full shadow-md">
                              <span className="text-xs font-bold text-white">50% off</span>
                            </div>
                          )}
                        </div>
                      </Link>

                      {/* Text Content */}
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                            {selectedListing.category}
                          </span>
                          <div className="flex items-center">
                            <i className="ri-star-fill text-yellow-400 text-xs mr-1"></i>
                            <span className="text-xs font-semibold text-gray-900">{selectedListing.rating}</span>
                            <span className="text-xs text-gray-500 ml-1">({selectedListing.reviews})</span>
                          </div>
                        </div>

                        <h3 className="text-sm font-bold text-gray-900 mb-1.5 line-clamp-2">
                          {selectedListing.title}
                        </h3>

                        <div className="flex items-center text-gray-600 mb-2">
                          <i className="ri-map-pin-line mr-1 text-xs"></i>
                          <span className="text-xs">{selectedListing.location}</span>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <div>
                            <span className="text-lg font-bold text-blue-600">${selectedListing.price}</span>
                            <span className="text-gray-600 ml-0.5 text-xs">/mo</span>
                          </div>
                          <Link
                            to={`/listing/${selectedListing.id}`}
                            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            View Details
                          </Link>
                        </div>
                        {selectedListing.availableFrom ? (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                              <i className="ri-calendar-line"></i>
                              Available {new Date(selectedListing.availableFrom) > new Date() 
                                ? `from ${new Date(selectedListing.availableFrom).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                : 'Now'}
                            </span>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                              <i className="ri-checkbox-circle-line"></i>
                              Available Now
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Arrow pointing to marker */}
                      <div 
                        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full"
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: '10px solid transparent',
                          borderRight: '10px solid transparent',
                          borderTop: '10px solid white',
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Listings Sidebar - Below map on mobile, left side on desktop */}
              <div className="w-full lg:w-2/5 order-2 lg:order-1 overflow-y-auto lg:h-[calc(100vh-400px)] lg:min-h-[600px] space-y-4 relative z-0 p-1">
                {displayedListings.length > 0 ? (
                  displayedListings.map((listing) => (
                    <Link
                      key={listing.id}
                      to={`/listing/${listing.id}`}
                      onMouseEnter={() => {
                        setHoveredListingId(listing.id);
                      }}
                      onMouseLeave={() => {
                        setHoveredListingId(null);
                      }}
                      className={`block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer ${
                        hoveredListingId === listing.id ? 'ring-2 ring-blue-600 ring-offset-2' : ''
                      }`}
                    >
                      <div className="flex gap-4 p-4">
                        <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg">
                          <img
                            src={listing.image}
                            alt={listing.title}
                            className="w-full h-full object-cover object-top"
                          />
                          <div className="absolute top-2 right-2 bg-blue-600 px-2 py-1 rounded-full shadow-md">
                            <span className="text-xs font-bold text-white">${listing.price}</span>
                          </div>
                          {listing.firstMonthDiscount && (
                            <div className="absolute top-2 left-2 bg-red-500 px-1.5 py-0.5 rounded-full shadow-md">
                              <span className="text-xs font-bold text-white">50% off</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                              {listing.category}
                            </span>
                            <div className="flex items-center">
                              <i className="ri-star-fill text-yellow-400 text-xs mr-1"></i>
                              <span className="text-xs font-semibold text-gray-900">{listing.rating}</span>
                              <span className="text-xs text-gray-500 ml-1">({listing.reviews})</span>
                            </div>
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2">
                            {listing.title}
                          </h3>
                          <div className="flex items-center text-gray-600 mb-2">
                            <i className="ri-map-pin-line mr-1 text-xs"></i>
                            <span className="text-xs">{listing.location}</span>
                          </div>
                          <div className="flex items-center text-gray-600 text-xs mb-2">
                            <i className="ri-expand-diagonal-line mr-1"></i>
                            <span>{listing.size}</span>
                            <span className="mx-1">•</span>
                            <span>{listing.exposure}</span>
                          </div>
                          <div className="text-sm font-bold text-blue-600">
                            ${listing.price}/mo
                          </div>
                          <div className="mt-2">
                            {listing.availableFrom ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                                <i className="ri-calendar-line"></i>
                                Available {new Date(listing.availableFrom) > new Date() 
                                  ? `from ${new Date(listing.availableFrom).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                  : 'Now'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                                <i className="ri-checkbox-circle-line"></i>
                                Available Now
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-16 bg-white rounded-xl">
                    <i className="ri-map-pin-line text-6xl text-gray-300 mb-4"></i>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No spaces in this area</h3>
                    <p className="text-gray-600">Zoom out or move the map to see more spaces</p>
                  </div>
                )}
              </div>
            </div>
          )
        ) : !loading && !fetchError ? (
          <div className="text-center py-16">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No spaces found</h3>
            <p className="text-gray-600">Try adjusting your filters or search in a different location</p>
          </div>
        ) : null}
      </div>

      <Footer />
    </div>
  );
}
